import { FC, useState } from 'react';
import { Box, FormLabel, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  clampDurationDaysInput,
  formatUpToDurationDays,
  parseDurationDaysValue,
} from '../../types/discoverExploreSearch.types';
import iconStyles from '../discoverFieldIcons.module.scss';

import styles from './DiscoverDurationDaysField.module.scss';

type DiscoverDurationDaysFieldProps = {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputLabel: string;
  error?: string;
  placeholder?: string;
  compactForMobile?: boolean;
  allowClear?: boolean;
  startIcon?: React.ReactNode;
};

const DiscoverDurationDaysField: FC<DiscoverDurationDaysFieldProps> = ({
  name,
  value,
  onChange,
  inputLabel,
  error,
  placeholder = 'Enter days',
  compactForMobile = false,
  allowClear = false,
  startIcon,
}) => {
  const [focused, setFocused] = useState(false);

  const parsed = parseDurationDaysValue(value);
  const showClear = allowClear && value.trim() !== '';

  const displayValue = (() => {
    if (focused) return value;
    if (parsed != null) return formatUpToDurationDays(parsed);
    return '';
  })();

  const emitValue = (next: string) => {
    onChange({
      target: { name, value: next },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    emitValue(clampDurationDaysInput(event.target.value));
  };

  const handleBlur = () => {
    setFocused(false);
    const next = clampDurationDaysInput(value);
    emitValue(next);
  };

  const fieldClass = [
    styles.textField,
    startIcon ? styles.textFieldWithLeadIcon : '',
    showClear ? styles.textFieldWithClear : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      className={styles.root}
      data-compact-mobile={compactForMobile ? 'true' : undefined}
    >
      <FormLabel className={styles.label}>
        <Typography
          variant="h11Semibold"
          color="#222B3A"
        >
          {inputLabel}
        </Typography>
      </FormLabel>

      <TextField
        name={name}
        value={displayValue}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        error={!!error}
        fullWidth
        variant="outlined"
        className={fieldClass}
        inputMode="numeric"
        autoComplete="off"
        InputProps={{
          startAdornment:
            startIcon != null ? (
              <InputAdornment
                position="start"
                className={iconStyles.adornmentTight}
              >
                {startIcon}
              </InputAdornment>
            ) : undefined,
          endAdornment: showClear ? (
            <InputAdornment position="end">
              <IconButton
                type="button"
                size="small"
                aria-label="Clear duration"
                edge="end"
                className={styles.clearButton}
                onMouseDown={e => e.preventDefault()}
                onClick={() => emitValue('')}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />

      <Box
        className={styles.errorSlot}
        role={error ? 'alert' : undefined}
      >
        {error ? (
          <Typography
            variant="h11"
            className={styles.errorText}
          >
            {error}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default DiscoverDurationDaysField;
